import React, { useState, useEffect, useRef } from 'react';

interface NotesProps {
  notes: string;
  onNotesChange: (notes: string) => void;
  height: string;
  onHeightChange: (height: string) => void;
  isAdmin?: boolean;
}

const Notes: React.FC<NotesProps> = ({ notes, onNotesChange, height, onHeightChange, isAdmin }) => {
  const [inputValue, setInputValue] = useState(notes);
  const [isComposing, setIsComposing] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    setInputValue(notes);
  }, [notes]);

  // Handle manual resize detection
  useEffect(() => {
    if (!textareaRef.current) return;

    resizeObserverRef.current = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === textareaRef.current) {
          // Using inline style height or clientHeight if needed, but style.height is set by resize handle
          const newHeight = (entry.target as HTMLElement).style.height;
          if (newHeight && newHeight !== height) {
            // Debounce height save to avoid excessive writes during drag
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
              onHeightChange(newHeight);
            }, 500);
          }
        }
      }
    });

    resizeObserverRef.current.observe(textareaRef.current);

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [height, onHeightChange]);

  const scheduleSave = (text: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      onNotesChange(text);
    }, 1000);
  };

  const handleComposition = (e: React.CompositionEvent<HTMLTextAreaElement>) => {
    if (e.type === 'compositionend') {
      setIsComposing(false);
      scheduleSave(e.currentTarget.value);
    } else {
      setIsComposing(true);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    if (!isComposing) {
      scheduleSave(e.target.value);
    }
  };

  return (
    <div className="h-full min-h-0">
      <textarea
        ref={textareaRef}
        value={inputValue}
        onCompositionStart={isAdmin ? handleComposition : undefined}
        onCompositionEnd={isAdmin ? handleComposition : undefined}
        onChange={isAdmin ? handleChange : undefined}
        readOnly={!isAdmin}
        placeholder={isAdmin ? "Jot down your notes here..." : "Notes (read-only)"}
        style={{ height, maxHeight: '100%' }}
        className={`block w-full overflow-y-auto bg-white/70 border-slate-300 text-light text-sm rounded-md p-2 focus:ring-accent focus:border-accent resize-y ${!isAdmin ? 'cursor-not-allowed opacity-70' : ''}`}
      ></textarea>
    </div>
  );
};

export default Notes;
